import { useCallback, useEffect, useRef, useState } from 'react';
import { collection, doc, onSnapshot, updateDoc, arrayUnion, runTransaction } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { errorReporter, isFirebaseOfflineError } from '@/services/errorReportingService';

export interface SavedNote {
  token: string;
  savedAt: string;
  /**
   * The viewer's own star on a watched note.
   *
   * Deliberately NOT the note's `is_favorite`: that is a single boolean on
   * `notes/{id}` owned by the note's creator, and the API only lets the owner
   * write it. A watched note belongs to someone else, so the star lives here —
   * on the viewer's own user doc, alongside the token that put it in their
   * list. Nothing a watcher does can reach the owner's star, and no API call
   * is involved.
   */
  is_favorite: boolean;
}

interface UseSavedNotesResult {
  savedNotes: SavedNote[];
  isLoading: boolean;
  isMutating: boolean;
  saveNote: (token: string) => Promise<void>;
  unsaveNote: (token: string) => Promise<void>;
  toggleNoteFavorite: (token: string, isFavorite: boolean) => Promise<void>;
  isNoteSaved: (token: string) => boolean;
}

const sanitizeSavedNotes = (raw: unknown): SavedNote[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const { token, savedAt, is_favorite: isFavorite } = item as Partial<SavedNote> & Record<string, unknown>;

      if (!token || typeof token !== 'string') {
        return null;
      }

      return {
        token,
        savedAt: typeof savedAt === 'string' ? savedAt : new Date().toISOString(),
        // Always a real boolean, never undefined: this shape is written back to
        // Firestore by the dedup transaction below, and Firestore rejects
        // undefined field values. Entries saved before stars existed read false.
        is_favorite: isFavorite === true,
      } satisfies SavedNote;
    })
    .filter((item): item is SavedNote => item !== null);
};

export const useSavedNotes = (userId?: string | null): UseSavedNotesResult => {
  const [savedNotes, setSavedNotes] = useState<SavedNote[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isMutating, setIsMutating] = useState<boolean>(false);
  const savedNotesRef = useRef<SavedNote[]>([]);
  const dedupInProgressRef = useRef(false);
  // Tail of the in-flight favorite write chain, per share token. See
  // toggleNoteFavorite for why these have to be serialized.
  const favoriteWritesRef = useRef<Map<string, Promise<void>>>(new Map());

  useEffect(() => {
    if (!userId) {
      setSavedNotes([]);
      savedNotesRef.current = [];
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);

    const docRef = doc(collection(db, 'users'), userId);

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        const data = snapshot.data();
        const sanitized = sanitizeSavedNotes(data?.saved_notes);

        // Auto-dedup: if duplicate tokens exist, keep earliest and clean Firestore
        const seen = new Set<string>();
        const deduped: SavedNote[] = [];
        for (const entry of sanitized) {
          if (!seen.has(entry.token)) {
            seen.add(entry.token);
            deduped.push(entry);
          }
        }

        if (deduped.length < sanitized.length && !dedupInProgressRef.current) {
          dedupInProgressRef.current = true;
          // Use a transaction to re-read and dedup atomically, avoiding overwrite races
          runTransaction(db, async (transaction) => {
            const snap = await transaction.get(docRef);
            const current = sanitizeSavedNotes(snap.data()?.saved_notes);
            const txSeen = new Set<string>();
            const txDeduped: SavedNote[] = [];
            for (const entry of current) {
              if (!txSeen.has(entry.token)) {
                txSeen.add(entry.token);
                txDeduped.push(entry);
              }
            }
            if (txDeduped.length < current.length) {
              transaction.update(docRef, { saved_notes: txDeduped });
            }
          })
            .catch((err) => errorReporter.expected('Failed to dedup saved notes', err, { feature: 'notes' }))
            .finally(() => {
              dedupInProgressRef.current = false;
            });
        }

        setSavedNotes(deduped);
        savedNotesRef.current = deduped;
        setIsLoading(false);
      },
      (error) => {
        errorReporter.expected('Failed to subscribe to saved notes', error, { feature: 'notes' });
        setSavedNotes([]);
        savedNotesRef.current = [];
        setIsLoading(false);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [userId]);

  const saveNote = useCallback(
    async (token: string) => {
      if (!userId) return;

      const trimmedToken = token.trim();
      if (!trimmedToken) return;

      const existing = savedNotesRef.current.find((item) => item.token === trimmedToken);
      if (existing) return;

      const docRef = doc(collection(db, 'users'), userId);
      const newEntry: SavedNote = {
        token: trimmedToken,
        savedAt: new Date().toISOString(),
        is_favorite: false,
      };

      // Optimistically add to ref so concurrent calls within the same mount
      // see this token and bail out via the `existing` check above.
      savedNotesRef.current = [...savedNotesRef.current, newEntry];

      setIsMutating(true);
      try {
        await updateDoc(docRef, { saved_notes: arrayUnion(newEntry) });
      } catch (error) {
        // Revert the optimistic ref so the user can retry
        savedNotesRef.current = savedNotesRef.current.filter((item) => item.token !== trimmedToken);
        if (isFirebaseOfflineError(error)) {
          errorReporter.expected('Failed to save note (client offline)', error, { feature: 'notes' });
        } else {
          errorReporter.critical('Failed to save note', error, { feature: 'notes' });
        }
        throw error;
      } finally {
        setIsMutating(false);
      }
    },
    [userId],
  );

  const unsaveNote = useCallback(
    async (token: string) => {
      if (!userId) return;

      const docRef = doc(collection(db, 'users'), userId);

      // Optimistically remove from local state so the UI updates instantly
      const previous = savedNotesRef.current;
      const updated = previous.filter((item) => item.token !== token);
      savedNotesRef.current = updated;
      setSavedNotes(updated);

      setIsMutating(true);
      try {
        // Use a transaction to atomically read-filter-write, avoiding race conditions
        // where concurrent additions from other tabs/devices could be lost.
        await runTransaction(db, async (transaction) => {
          const snap = await transaction.get(docRef);
          const current: unknown[] = Array.isArray(snap.data()?.saved_notes) ? snap.data()!.saved_notes : [];
          const filtered = current.filter((item) => {
            if (!item || typeof item !== 'object') return true;
            return (item as Record<string, unknown>).token !== token;
          });
          transaction.update(docRef, { saved_notes: filtered });
        });
      } catch (error) {
        // Revert the optimistic removal so UI stays in sync
        savedNotesRef.current = previous;
        setSavedNotes(previous);
        if (isFirebaseOfflineError(error)) {
          errorReporter.expected('Failed to unsave note (client offline)', error, { feature: 'notes' });
        } else {
          errorReporter.critical('Failed to unsave note', error, { feature: 'notes' });
        }
        throw error;
      } finally {
        setIsMutating(false);
      }
    },
    [userId],
  );

  const toggleNoteFavorite = useCallback(
    async (token: string, isFavorite: boolean) => {
      if (!userId) return;

      const docRef = doc(collection(db, 'users'), userId);

      // Optimistically flip locally so the star responds immediately.
      const previous = savedNotesRef.current;
      const updated = previous.map((item) => (item.token === token ? { ...item, is_favorite: isFavorite } : item));
      savedNotesRef.current = updated;
      setSavedNotes(updated);

      const write = async () => {
        setIsMutating(true);
        try {
          // arrayUnion can't edit an entry in place, so this is a read-modify-write
          // of the whole array — in a transaction for the same reason unsaveNote
          // uses one: a concurrent save from another tab would otherwise be lost.
          await runTransaction(db, async (transaction) => {
            const snap = await transaction.get(docRef);
            const current = sanitizeSavedNotes(snap.data()?.saved_notes);
            if (!current.some((item) => item.token === token)) {
              // Unwatched between render and write — nothing to star.
              return;
            }
            const next = current.map((item) => (item.token === token ? { ...item, is_favorite: isFavorite } : item));
            transaction.update(docRef, { saved_notes: next });
          });
        } catch (error) {
          savedNotesRef.current = previous;
          setSavedNotes(previous);
          if (isFirebaseOfflineError(error)) {
            errorReporter.expected('Failed to star watched note (client offline)', error, { feature: 'notes' });
          } else {
            errorReporter.critical('Failed to star watched note', error, { feature: 'notes' });
          }
          throw error;
        } finally {
          setIsMutating(false);
        }
      };

      // Serialize writes per token. Nothing disables the star while a write is
      // in flight, so double-clicking one starts two transactions that each
      // close over their own target value — and Firestore may retry and commit
      // them in either order, letting the OLDER click land last and persist the
      // wrong state. Chaining per token guarantees the last click writes last.
      const inFlight = favoriteWritesRef.current.get(token) ?? Promise.resolve();
      const queued = inFlight.then(write, write);
      // Swallow on the stored handle only — the returned promise still rejects
      // so callers can react, but a rejection here must not break the chain for
      // the next click, and an unobserved stored rejection would warn.
      const stored = queued.catch(() => {});
      favoriteWritesRef.current.set(token, stored);

      try {
        await queued;
      } finally {
        // Only the tail clears the entry; a newer click will have replaced it.
        if (favoriteWritesRef.current.get(token) === stored) {
          favoriteWritesRef.current.delete(token);
        }
      }
    },
    [userId],
  );

  const isNoteSaved = useCallback(
    (token: string) => {
      return savedNotes.some((item) => item.token === token);
    },
    [savedNotes],
  );

  return {
    savedNotes,
    isLoading,
    isMutating,
    saveNote,
    unsaveNote,
    toggleNoteFavorite,
    isNoteSaved,
  };
};
